import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { adminUpdateStatus, adminAddNote, adminSetInterviewSlots, getAvailability } from "../../lib/api";
import { US_TIMEZONES, DEFAULT_TZ, wallToUTCISO, isoToWallInput, fmtInTz, nextWallForWeekday } from "../../lib/timezones";

const STAGES = ["Submitted", "Under Review", "Shortlisted", "Interviewing", "Offer", "Hired", "Not Selected", "Withdrawn"];

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminManageDialog({ app, adminKey, open, onOpenChange, onChanged }) {
  const [status, setStatus] = useState(app?.status || "Submitted");
  const [statusNote, setStatusNote] = useState("");
  const [history, setHistory] = useState(app?.status_history || []);
  const [notes, setNotes] = useState(app?.notes || []);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tz, setTz] = useState(app?.interview_timezone || DEFAULT_TZ);
  const [slots, setSlots] = useState(
    (app?.interview_slots || []).map((s) => isoToWallInput(s, app?.interview_timezone || DEFAULT_TZ)).filter(Boolean)
  );
  const [location, setLocation] = useState(app?.interview_location || "");
  const [booked, setBooked] = useState(app?.interview_booked || null);

  if (!app) return null;

  const addSlot = () => setSlots((s) => [...s, ""]);
  const setSlotAt = (i, v) => setSlots((s) => s.map((x, idx) => (idx === i ? v : x)));
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));

  const useAvailability = async () => {
    try {
      const av = await getAvailability(adminKey);
      if (!av.slots || av.slots.length === 0) {
        toast.info("No weekly availability set", { description: "Add it under the Availability tab first." });
        return;
      }
      const tzToUse = av.timezone || DEFAULT_TZ;
      setTz(tzToUse);
      const generated = av.slots
        .map((s) => nextWallForWeekday(s.weekday, s.time, tzToUse, av.blackouts || []))
        .filter(Boolean)
        .sort();
      setSlots(generated);
      toast.success("Filled from your weekly availability", { description: `${generated.length} time(s) added — review and send.` });
    } catch {
      toast.error("Could not load your availability");
    }
  };

  const sendInterview = async () => {
    const iso = slots.filter(Boolean).map((v) => wallToUTCISO(v, tz)).filter(Boolean);
    if (iso.length === 0) { toast.error("Add at least one interview time"); return; }
    setBusy(true);
    try {
      await adminSetInterviewSlots(app.id, { slots: iso, location, timezone: tz }, adminKey);
      setBooked(null);
      onChanged && onChanged(app.id, { interview_slots: iso, interview_location: location, interview_timezone: tz, interview_booked: null });
      toast.success("Interview invite sent", { description: "The candidate can now pick a time." });
    } catch { toast.error("Failed to send interview invite"); }
    finally { setBusy(false); }
  };

  const changeStatus = async () => {
    setBusy(true);
    try {
      await adminUpdateStatus(app.id, { status, note: statusNote }, adminKey);
      const entry = { status, at: new Date().toISOString(), note: statusNote };
      setHistory((h) => [...h, entry]);
      setStatusNote("");
      onChanged && onChanged(app.id, { status });
      toast.success(`Status set to ${status}`, { description: "The candidate has been emailed." });
    } catch { toast.error("Failed to update status"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const res = await adminAddNote(app.id, noteText, adminKey);
      setNotes((n) => [...n, res.entry]);
      setNoteText("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-ink-800 text-white sm:max-w-2xl" data-testid="admin-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight">{app.name}</DialogTitle>
          <DialogDescription className="text-slate-400">Manage status, notes and interview scheduling for this application.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-400">{app.email} · {app.role || "General Application"}</p>

        <div className="mt-4 grid gap-6 md:grid-cols-2">
          {/* Status */}
          <div className="rounded-xl border border-white/10 bg-ink-900/50 p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Update Status</h3>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-3 w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70" data-testid="manage-status-select">
              {STAGES.map((s) => <option key={s} value={s} className="bg-ink-900">{s}</option>)}
            </select>
            <textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={2} placeholder="Optional note for the candidate email" className="mt-3 w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70" data-testid="manage-status-note" />
            <button onClick={changeStatus} disabled={busy} className="mt-3 w-full rounded-full bg-sky-400 px-4 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="manage-status-save">Save status & notify</button>
          </div>

          {/* Status history */}
          <div className="rounded-xl border border-white/10 bg-ink-900/50 p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Status History</h3>
            <ul className="mt-3 space-y-3">
              {[...history].reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-400" />
                  <div>
                    <div className="text-sm font-medium text-white">{h.status}</div>
                    <div className="text-xs text-slate-500">{fmt(h.at)}</div>
                    {h.note && <p className="mt-0.5 text-xs text-slate-400">{h.note}</p>}
                  </div>
                </li>
              ))}
              {history.length === 0 && <li className="text-sm text-slate-500">No history yet.</li>}
            </ul>
          </div>
        </div>

        {/* Recruiter notes */}
        <div className="mt-6 rounded-xl border border-white/10 bg-ink-900/50 p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Recruiter Notes</h3>
          <ul className="mt-3 space-y-3">
            {[...notes].reverse().map((n, i) => (
              <li key={i} className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
                <p className="text-sm text-slate-200">{n.note}</p>
                <div className="mt-1 text-xs text-slate-500">{fmt(n.at)}</div>
              </li>
            ))}
            {notes.length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
          </ul>
          <div className="mt-4 flex gap-2">
            <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note..." className="flex-1 rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70" data-testid="manage-note-input" />
            <button onClick={addNote} disabled={busy} className="rounded-full bg-sky-400 px-4 py-2 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="manage-note-add">Add</button>
          </div>
        </div>

        {/* Interview scheduling */}
        <div className="mt-6 rounded-xl border border-white/10 bg-ink-900/50 p-5" data-testid="manage-interview">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Interview Scheduling</h3>
          {booked ? (
            <div className="mt-3 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200" data-testid="manage-interview-booked">
              Candidate booked: <strong>{fmtInTz(booked, tz)}</strong>{location ? ` · ${location}` : ""}
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">Propose one or more times; the candidate picks one from their portal and both of you get an email.</p>
          )}
          <div className="mt-3">
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">Time zone (candidate sees times in this zone)</label>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
              data-testid="manage-interview-tz"
            >
              {US_TIMEZONES.map((t) => (
                <option key={t.value} value={t.value} className="bg-ink-900">{t.label}</option>
              ))}
            </select>
          </div>
          <div className="mt-3 space-y-2">
            {slots.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={s}
                  onChange={(e) => setSlotAt(i, e.target.value)}
                  className="flex-1 rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70 [color-scheme:dark]"
                  data-testid={`manage-interview-slot-${i}`}
                />
                <button onClick={() => removeSlot(i)} className="rounded-lg border border-white/10 px-3 py-2 text-xs text-slate-400 hover:text-red-300" data-testid={`manage-interview-remove-${i}`}>Remove</button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <button onClick={addSlot} className="text-sm font-medium text-sky-400 hover:text-sky-300" data-testid="manage-interview-add-slot">+ Add time</button>
            <button onClick={useAvailability} className="text-sm font-medium text-sky-400 hover:text-sky-300" data-testid="manage-interview-use-availability">Use weekly availability</button>
          </div>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Location or video link (optional)"
            className="mt-3 w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
            data-testid="manage-interview-location"
          />
          <button onClick={sendInterview} disabled={busy} className="mt-3 w-full rounded-full bg-sky-400 px-4 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="manage-interview-send">
            {slots.length > 0 && booked ? "Re-send interview invite" : "Send interview invite"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
